import {
  Quote,
  HedgingSolution,
  HedgingInstruments,
  CalculationState,
  QuoteState,
  isValidQuote,
  VersionedObject,
} from 'ah-api-gateways';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { makeStoreSupportData, StoreSupportData } from './supportData';
import { generateUID } from 'ah-common-lib/src/helpers/uuid';
import { HttpOptions, RequestState, waitForCQRSEntityChange, waitForEntityCreation } from 'ah-requests';
import cloneDeep from 'lodash/cloneDeep';
import { commonStoreActions } from 'ah-common-lib/src/constants/storeActions';
import { useAuthStore } from './authModule';
import { defineStore } from 'pinia';

let sd!: StoreSupportData;

export enum QuoteRequestName {
  getQuote = 'getQuote',
  updateQuote = 'updateQuote',
  updateQuoteCall = 'updateQuoteCall',
  refreshRates = 'refreshRates',
  approveSolution = 'approveSolution',
  createQuote = 'createQuote',
  updateQuoteSolution = 'updateQuoteSolution',
}

function findSolution(solutions: HedgingSolution[], solutionId?: string, solutionTempId?: string) {
  return solutions.find((s) => (solutionId && s.id === solutionId) || (solutionTempId && s.$tempID === solutionTempId));
}

function findQuoteSolution(quote: Quote, solutionId?: string, solutionTempId?: string) {
  return findSolution(quote.solutions, solutionId, solutionTempId);
}

function getQuoteRequestName(updateName: QuoteRequestName = QuoteRequestName.getQuote, quoteId = 'NEW') {
  return `${updateName}-${quoteId}`;
}

function getUpdateQuoteSolutionName(solutionId = '') {
  return `${QuoteRequestName.updateQuoteSolution}-${solutionId}`;
}

function checkQuoteUpdateAllowed(quote: Quote): Promise<void> {
  const authStore = useAuthStore();

  if (quote.$deletedClient) {
    return Promise.reject("Quote's Client has been deleted, can't update.");
  }
  if (!authStore.loggedInIdentity!.settings?.quoteCallRequired) {
    return Promise.resolve();
  }

  return Promise.resolve();
}

/**
 * Quote Store
 *
 * Warning: sync/persistence off, as it may lead to unexpected behaviours
 */
export const useQuoteStore = defineStore('quoteModule', {
  persist: false,
  state: () => {
    if (!sd) {
      sd = makeStoreSupportData();
    }
    return {
      _quotes: [] as Quote[],
      _requestStates: {} as { [key: string]: RequestState },
    };
  },
  getters: {
    getSortedSavedSolutions() {
      return (quoteId: string) => {
        return (
          this.getQuote(quoteId)
            ?.solutions.filter((s) => s.id)
            .sort((a, b) => a.displayOrder! - b.displayOrder!) || []
        );
      };
    },
    getUnsavedSolutions() {
      return (quoteId: string) => {
        return this.getQuote(quoteId)?.solutions.filter((s) => !s.id) || [];
      };
    },
    canProduceTermsheet() {
      return (quote?: Quote) =>
        quote && this.isValidQuote(quote) && this.areAllSolutionsValid(quote) && !this.updating(quote);
    },
    updating() {
      return (quote: Quote) =>
        this.getQuoteRefreshState(quote.id) === 'pending' ||
        this.getQuoteUpdateState(quote.id) === 'pending' ||
        this.getQuoteConfirmSolutionState(quote.id) === 'pending';
    },
    isValidQuote() {
      return (quote: Quote) => isValidQuote(quote) && quote.solutions.length;
    },
    solutions() {
      return (quote: Quote) => [...this.draftSolutions(quote), ...this.sortedSavedSolutions(quote)];
    },
    areAllSolutionsValid() {
      return (quote: Quote) =>
        this.solutions(quote).filter(
          (s, index) =>
            !s.id ||
            s.calculationState !== CalculationState.SUCCESSFULLY ||
            this.getSolutionUpdateStates(quote.id)[index] !== 'idle'
        ).length === 0;
    },

    draftSolutions() {
      return (quote: Quote) => ((quote && quote.solutions) || []).filter((s) => !s.id);
    },
    sortedSavedSolutions() {
      return (quote: Quote) =>
        ((quote && quote.solutions) || []).filter((s) => s.id).sort((a, b) => a.displayOrder! - b.displayOrder!);
    },
    quotes(state) {
      return state._quotes;
    },
    getQuote(state) {
      return (quoteId: string) => {
        return state._quotes.find((q) => q.id === quoteId);
      };
    },
    isQuoteUpdateAllowed() {
      return (quoteId: string) => {
        const quote = this.getQuote(quoteId);
        return quote && checkQuoteUpdateAllowed(quote);
      };
    },
    getQuoteUpdateState(state) {
      return (quoteId: string) => state._requestStates[getQuoteRequestName(QuoteRequestName.updateQuote, quoteId)];
    },
    getQuoteRefreshState(state) {
      return (quoteId: string) => state._requestStates[getQuoteRequestName(QuoteRequestName.refreshRates, quoteId)];
    },
    getQuoteLoadState(state) {
      return (quoteId: string) => state._requestStates[getQuoteRequestName(QuoteRequestName.getQuote, quoteId)];
    },
    getQuoteConfirmSolutionState(state) {
      return (quoteId: string) => state._requestStates[getQuoteRequestName(QuoteRequestName.approveSolution, quoteId)];
    },
    getSolutionUpdateStates(state) {
      return (quoteId: string) => {
        const quote = state._quotes.find((q) => q.id === quoteId);

        if (!quote || !quote.solutions) {
          return [];
        }

        return this.solutions(quote).map(
          (s) => state._requestStates[getUpdateQuoteSolutionName(s.id || s.$tempID)] || 'idle'
        );
      };
    },
  },
  actions: {
    // FIXME this should be replaced by a server-set prop when possible
    async markQuoteAsOrphan(quoteId: string) {
      const quote = this.getQuote(quoteId);

      if (quote) {
        this._updateQuoteObject({ quote: { ...quote, $deletedClient: true } });
      }
    },
    async [commonStoreActions.onSetup]() {
      sd = makeStoreSupportData();
      this._requestStates = sd.reqManager.requestStates;
    },
    async [commonStoreActions.onLogout]() {
      sd = makeStoreSupportData();
      this._requestStates = sd.reqManager.requestStates;

      this._quotes = [];
    },
    createQuote(payload: { clientId: string }) {
      const { clientId } = payload;
      const authStore = useAuthStore();

      const agent = authStore.loggedInIdentity!;

      return sd.s.quote
        .createQuote(agent.id, clientId)
        .pipe(
          mergeMap((idEntity) =>
            waitForEntityCreation(() => sd.s.quote.getQuote(idEntity.id, { errors: { silent: true } }))
          )
        )
        .toPromise()
        .then((quote) => {
          this._updateQuoteObject({ quote, keepTempSolutions: false });
          return quote;
        });
    },
    refreshRates(payload: { quoteId: string }) {
      const { quoteId } = payload;
      return sd.reqManager
        .sameOrCancelAndNew(
          getQuoteRequestName(QuoteRequestName.refreshRates, quoteId),
          sd.s.quote
            .refreshQuote(quoteId)
            .pipe(
              mergeMap((idEntity) =>
                waitForCQRSEntityChange(idEntity, () =>
                  sd.s.quote.getQuote(idEntity.id, { errors: { silent: true } })
                ).pipe(catchError(() => of(this.getQuote(quoteId)!)))
              )
            )
        )
        .pipe(tap((quote) => this._updateQuoteObject({ quote })))
        .toPromise();
    },
    updateQuote(payload: {
      quoteId: string;
      update: Partial<Quote>;
      keepTempSolutions?: boolean;
      options?: HttpOptions<VersionedObject>;
    }) {
      const { quoteId, update, keepTempSolutions, options } = { keepTempSolutions: true, ...payload };

      const quote = this.getQuote(quoteId);

      if (!quote) {
        return Promise.reject(`No quote for id ${quoteId} in store`);
      }

      return checkQuoteUpdateAllowed(quote)
        .then(() =>
          sd.reqManager
            .sameOrCancelAndNew(
              getQuoteRequestName(QuoteRequestName.updateQuote, quoteId),
              sd.s.quote
                .updateQuote(quoteId, update, options)
                .pipe(
                  mergeMap((idEntity) =>
                    waitForCQRSEntityChange(idEntity, () =>
                      sd.s.quote.getQuote(idEntity.id, { errors: { silent: true } })
                    )
                  )
                ),
              update
            )
            .toPromise()
        )
        .then((quote) => {
          this._updateQuoteObject({ quote, keepTempSolutions });
          return quote;
        });
    },
    loadQuote(payload: { quoteId: string; keepTempSolutions?: boolean }) {
      const { quoteId, keepTempSolutions } = { keepTempSolutions: true, ...payload };
      return sd.reqManager
        .sameOrCancelAndNew(getQuoteRequestName(QuoteRequestName.getQuote, quoteId), sd.s.quote.getQuote(quoteId))
        .toPromise()
        .then((quote) => cloneDeep(quote))
        .then((quote) => {
          this._updateQuoteObject({ quote, keepTempSolutions });
          return quote;
        });
    },
    async addTemporarySolution(payload: { quoteId: string; instrument: HedgingInstruments }) {
      const { quoteId } = payload;
      const quote = this.getQuote(quoteId);

      if (!quote) {
        return Promise.reject(`No quote for id ${quoteId} in store`);
      }

      return checkQuoteUpdateAllowed(quote).then(() => {
        this._addTemporarySolution(payload);
      });
    },
    removeSolution(payload: { quoteId: string; solution: HedgingSolution }) {
      const { solution, quoteId } = payload;
      const quote = this.getQuote(quoteId);

      if (!quote) {
        return Promise.reject(`No quote for id ${quoteId} in store`);
      }

      return checkQuoteUpdateAllowed(quote).then(() => {
        this._removeSolution({ quoteId, solution });
        if (solution.id) {
          return sd.s.quote.removeQuoteSolution(quoteId, solution.id).toPromise();
        }
      });
    },
    acceptQuoteSolution(payload: { quoteId: string; solutionId: string }) {
      const { quoteId, solutionId } = payload;
      const quote = this.getQuote(quoteId);

      if (!quote) {
        return Promise.reject(`No quote for id ${quoteId} in store`);
      }

      const solution = findQuoteSolution(quote, solutionId);

      if (!solution) {
        return Promise.reject(`No solution for id ${solutionId} in store`);
      }

      return sd.reqManager
        .cancelAndNew(
          getQuoteRequestName(QuoteRequestName.approveSolution, quoteId),
          sd.s.quote.expressInterest(quoteId, solutionId)
        )
        .toPromise()
        .then(() => {
          this._setQuoteSolutionApproval({
            quoteId,
            solutionId,
          });
        });
    },
    updateQuoteSolution(payload: { quoteId: string; solutionId: string; update: Partial<HedgingSolution> }) {
      let request!: Observable<VersionedObject>;
      const { quoteId, solutionId, update } = payload;

      const quote = this.getQuote(quoteId);
      if (!quote) {
        return Promise.reject(`No quote for id ${quoteId}`);
      }
      return checkQuoteUpdateAllowed(quote).then(() => {
        const solution = findQuoteSolution(quote, solutionId, solutionId);

        if (solution && solution.id) {
          request = sd.s.quote.updateQuoteSolution(quoteId, solution.id, {
            ...solution,
            ...update,
          });
        } else {
          request = sd.s.quote.addQuoteSolution(quoteId, {
            ...solution,
            ...update,
          });
        }

        return sd.reqManager
          .sameOrCancelAndNew(getUpdateQuoteSolutionName(solutionId), request, update)
          .pipe(
            mergeMap((idEntity) => {
              let request;
              if (solution && solution.id) {
                request = waitForCQRSEntityChange(idEntity, () =>
                  sd.s.quote.getQuoteSolution(quoteId, idEntity.id, { errors: { silent: true } })
                );
              } else {
                request = waitForEntityCreation(() =>
                  sd.s.quote.getQuoteSolution(quoteId, idEntity.id, { errors: { silent: true } })
                );
              }
              return request.pipe(catchError(() => of({ ...solution, ...update } as HedgingSolution)));
            })
          )
          .toPromise()
          .then((updatedSolution) =>
            this._updateQuoteSolutionObject({
              quoteId,
              solutionTempId: solution?.$tempID,
              solution: updatedSolution,
            })
          );
      });
    },
    sortSolution(payload: { quoteId: string; solution: HedgingSolution; delta: number }) {
      const { quoteId, solution, delta } = payload;
      const quote = this.getQuote(quoteId);

      if (!quote) {
        return Promise.reject(`No quote for id ${quoteId} in store`);
      }

      if (!solution.id) {
        return Promise.reject('Unsaved solutions cannot be sorted');
      }

      return checkQuoteUpdateAllowed(quote).then(() => {
        const solutions = this.getSortedSavedSolutions(quoteId);
        const index = solutions.indexOf(solution);

        if (index + delta >= 0 && index + delta < solutions.length) {
          const startOrder = solutions[index].displayOrder;

          solutions[index].displayOrder = solutions[index + delta].displayOrder;
          solutions[index + delta].displayOrder = startOrder;
        }
        this._setQuoteSolutions({ quoteId, solutions: [...this.getUnsavedSolutions(quoteId), ...solutions] });
        return this.saveSolutionOrder({ quoteId });
      });
    },
    _setQuoteSolutionApproval(payload: { quoteId: string; solutionId: string }) {
      const { quoteId, solutionId } = payload;
      const quote = this._quotes.find((q) => q.id === quoteId);

      if (!quote) {
        return;
      }

      const solution = findSolution(quote.solutions, solutionId);

      if (!solution) {
        return;
      }

      quote.state = QuoteState.CLIENT_EXPRESSED_INTEREST;
      solution.expressedInterest = true;
    },
    _setQuoteSolutions(payload: { quoteId: string; solutions: HedgingSolution[] }) {
      const { quoteId, solutions } = payload;
      const quote = this._quotes.find((q) => q.id === quoteId);

      if (quote) {
        quote.solutions = solutions;
      }
    },
    _addTemporarySolution(payload: { quoteId: string; instrument: HedgingInstruments }) {
      const { instrument, quoteId } = payload;
      const quote = this._quotes.find((q) => q.id === quoteId);

      if (quote) {
        quote.solutions = quote.solutions || [];
        const commonDate = quote.solutions.map((s) => s.expiryDate).find((d) => !!d);

        quote.solutions.push({
          calculationState: CalculationState.UNCALCULATED,
          hedgingProduct: instrument,
          expiryDate: commonDate,
          ccy1: {} as any,
          ccy2: {} as any,
          createdAt: '',
          expressedInterest: false,
          forwardRate: 0,
          $tempID: generateUID(6),
        } as HedgingSolution);
      }
    },
    _removeSolution(payload: { quoteId: string; solution: HedgingSolution }) {
      const { quoteId, solution } = payload;
      const quote = this._quotes.find((q) => q.id === quoteId);

      if (quote && quote.solutions) {
        const solutionIndex = quote.solutions.indexOf(solution);
        if (solutionIndex > -1) {
          quote.solutions.splice(solutionIndex, 1);
        }
      }
    },
    _updateQuoteObject(payload: { quote: Quote; keepTempSolutions?: boolean }) {
      const { quote, keepTempSolutions } = { keepTempSolutions: true, ...payload };
      const savedQuote = this._quotes.find((q) => q.id === quote.id);
      let tempSolutions: HedgingSolution[] = [];

      if (quote.$deletedClient === undefined) {
        quote.$deletedClient = savedQuote?.$deletedClient;
      }

      if (keepTempSolutions && savedQuote) {
        tempSolutions = savedQuote.solutions.filter((s) => !s.id);

        savedQuote.solutions.forEach((solution) => {
          if (solution.id && solution.$tempID) {
            const updatedSolution = quote.solutions.find((s) => s.id === solution.id);

            if (updatedSolution) {
              updatedSolution.$tempID = solution.$tempID;
            }
          }
        });
      }

      if (savedQuote) {
        this._quotes.splice(this._quotes.indexOf(savedQuote), 1, quote);
      } else {
        this._quotes.push(quote);
      }
      quote.solutions = quote.solutions || [];
      quote.solutions.push(...tempSolutions);
    },
    async saveSolutionOrder(payload: { quoteId: string }) {
      const quote = this.getQuote(payload.quoteId);
      const solutions = this.getSortedSavedSolutions(payload.quoteId);

      if (quote) {
        return sd.reqManager
          .cancelAndNew(
            'updateSolutionOrder',
            sd.s.quote
              .updateSolutionOrder(
                quote!.id,
                solutions.map((s) => s.id!)
              )
              .pipe(
                mergeMap((idEntity) =>
                  waitForCQRSEntityChange(idEntity, () =>
                    sd.s.quote.getQuote(idEntity.id, { errors: { silent: true } })
                  )
                ),
                tap((quote) => this._updateQuoteObject({ quote }))
              )
          )
          .toPromise();
      }
      return Promise.reject('No quote saved for this id!');
    },
    async _updateQuoteSolutionObject(payload: { quoteId: string; solution: HedgingSolution; solutionTempId?: string }) {
      const { quoteId, solution, solutionTempId } = payload;
      const quote = this.getQuote(quoteId);

      if (!quote) {
        return;
      }
      const solutions = [...quote.solutions];

      const currSolution = findSolution(solutions, solution.id, solutionTempId);

      if (currSolution?.$tempID) {
        solution.$tempID = currSolution.$tempID;
      }

      if (currSolution) {
        solutions.splice(solutions.indexOf(currSolution), 1, solution);
        if (!currSolution.id) {
          this.getSortedSavedSolutions(quote.id).forEach((s) => (s.displayOrder = s.displayOrder! + 1));
          solution.displayOrder = 0;
          setTimeout(() => this.saveSolutionOrder({ quoteId }));
        }
      } else {
        solutions.push(solution);
      }
      this._setQuoteSolutions({ quoteId, solutions });
      return solution;
    },
  },
});
